import React, { useState } from 'react';
import { LockIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Tabs } from '../../components/ui/Tabs';
import { LedgerList } from '../../components/money/LedgerList';
import { capabilityGates, reconciliation, transactions } from '../../data/finance';
import { myOrders } from '../../data/marketplace';
import { OrderStateBadge } from '../../components/marketplace/OrderStateTrack';
import { naira } from '../../utils/format';
const TABS = ['Reconciliation', 'Transactions', 'Escrow', 'Capabilities'] as const;
export function FinanceOperations() {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Reconciliation');
  const escrowOrders = myOrders.filter(order => ['IN_ESCROW', 'IN_PROGRESS', 'DELIVERED', 'BUYER_REVIEW', 'DISPUTED'].includes(order.state));
  const escrowTotal = escrowOrders.reduce((total, order) => total + order.amountNaira, 0);
  const unmatched = reconciliation.reduce((total, row) => total + row.unmatched, 0);
  return <div className="space-y-6" data-id="element-6635" data-mp="37oi8vck051zc">
      <PageHeader eyebrow="Finance operations" title="Ledger, reconciliation and settlement" description="Separated from academic, wellbeing and safety data. Finance administrators see money movement only." actions={<Badge tone="warn" icon={<LockIcon className="h-3.5 w-3.5" data-id="element-6638" data-mp="3lelo3jkgrvfa" />} data-id="element-6637" data-mp="3lfwvruh15nbo">
            Scoped access — attributed and logged
          </Badge>} data-id="element-6636" data-mp="37lmdnvzjgrel" />

      <div className="grid gap-4 sm:grid-cols-3" data-id="element-6639" data-mp="3qg18d9ethxwm">
        {[{
        label: 'Held in escrow',
        value: naira(escrowTotal),
        note: `${escrowOrders.length} open orders`
      }, {
        label: 'Unmatched records',
        value: String(unmatched),
        note: 'Across all providers'
      }, {
        label: 'Settlement difference',
        value: naira(reconciliation.reduce((total, row) => total + row.difference, 0)),
        note: 'Requires investigation'
      }].map((card, index) => <div key={card.label} className={`rounded-card border bg-surface px-5 py-4 ${index === 0 ? 'border-brand-700' : 'border-line'}`} data-id="element-6640" data-mp="1syly481ussrf" data-mpi={index}>
            <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-6641" data-mp="22x1lbzxrfhvn">
              {card.label}
            </p>
            <p className="cz-num mt-2 text-2xl font-semibold text-ink" data-id="element-6642" data-mp="2gv8ckx87ejmc">{card.value}</p>
            <p className="mt-1.5 text-[12px] text-ink-muted" data-id="element-6643" data-mp="2bweyb2l3eol4">{card.note}</p>
          </div>)}
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Finance sections" data-id="element-6644" data-mp="2ql1dn4apzgcc" />

      {tab === 'Reconciliation' && <Panel title="Provider reconciliation" description="Campuzon ledger against every external rail." bodyClassName="p-0" data-id="element-6645" data-mp="2lo1frgo0xjwb">
          <div className="cz-tablewrap" data-id="element-6646" data-mp="2vmgwcppfdark">
          <table className="w-full text-left" data-id="element-6647" data-mp="2vjcnvg4xfsv3">
            <thead className="border-b border-line" data-id="element-6648" data-mp="3e9hs71lejcp9">
              <tr className="text-[12px] uppercase tracking-wide text-ink-subtle" data-id="element-6649" data-mp="3e7n7noe1lmlp">
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-6650" data-mp="3j81ckxkckgfi">
                  Source
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-6651" data-mp="35be3xxj181tr">
                  Matched
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-6652" data-mp="2viq9vajfsbyq">
                  Unmatched
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-6653" data-mp="30aqe3f6jhr14">
                  Difference
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-6654" data-mp="2mgh22sfjywp1">
                  Last checked
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-6655" data-mp="2clp4vppvubus">
              {reconciliation.map(row => <tr key={row.source} data-id="element-6656" data-mp="2hixlvmzao9ug">
                  <td className="px-5 py-3 text-[13px] text-ink" data-id="element-6657" data-mp="3jwvhlu8mtami">{row.source}</td>
                  <td className="cz-num px-5 py-3 text-right text-[13px] text-ink-muted" data-id="element-6658" data-mp="3jyzi027bnri1">
                    {row.matched.toLocaleString()}
                  </td>
                  <td className="cz-num px-5 py-3 text-right text-[13px]" data-id="element-6659" data-mp="3b3jl1cb9sw88">
                    {row.unmatched > 0 ? <Badge tone="warn" data-id="element-6660" data-mp="365882spoi5ls">{row.unmatched}</Badge> : <span className="text-ink-muted" data-id="element-6661" data-mp="317ynprh5ck4t">0</span>}
                  </td>
                  <td className="cz-num px-5 py-3 text-right text-[13px] text-ink-muted" data-id="element-6662" data-mp="2w5r7uqt74jx7">
                    {row.difference === 0 ? '—' : naira(row.difference)}
                  </td>
                  <td className="cz-num px-5 py-3 text-right text-[12px] text-ink-subtle" data-id="element-6663" data-mp="2n7xk449hyyln">
                    {row.checked}
                  </td>
                </tr>)}
            </tbody>
          </table>
          </div>
          <div className="border-t border-line px-5 py-3" data-id="element-6664" data-mp="ldeusd9uid07">
            <p className="text-[12px] leading-5 text-ink-muted" data-id="element-6665" data-mp="sxz7k57vxhfu">
              Reconciliation detects missing and duplicate transactions, unmatched provider
              references, settlement differences, incorrect fees and failed reversals. Discrepancies
              are investigated by a person — never auto-corrected.
            </p>
          </div>
        </Panel>}

      {tab === 'Transactions' && <Panel title="Transaction search" description="Every record is immutable and expandable to its paired entries." bodyClassName="p-0" data-id="element-6666" data-mp="a1qyeqbr4mf9">
          <LedgerList transactions={transactions} data-id="element-6667" data-mp="jyc1h647nngg" />
        </Panel>}

      {tab === 'Escrow' && <Panel title="Open escrow" bodyClassName="p-0" data-id="element-6668" data-mp="o073ydxvo6vt">
          <ul className="divide-y divide-line" data-id="element-6669" data-mp="nxkweeazlrdl">
            {escrowOrders.map(order => <li key={order.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-6670" data-mp="1vczg40ru2e38">
                <div className="min-w-0 flex-1" data-id="element-6671" data-mp="1zd9mhejjf6im">
                  <p className="text-[13px] font-medium text-ink" data-id="element-6672" data-mp="9kx42d0r6fh">{order.title}</p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-6673" data-mp="3rgdv6xcimpfs">
                    {order.reference} · {order.counterparty} · {order.dueLabel}
                  </p>
                </div>
                <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-6674" data-mp="4boa4vfc6j2v">
                  {naira(order.amountNaira)}
                </span>
                <OrderStateBadge state={order.state} data-id="element-6675" data-mp="3csizr974i6z0" />
              </li>)}
          </ul>
          <div className="border-t border-line px-5 py-3" data-id="element-6676" data-mp="38r79r2kc7jqa">
            <p className="text-[12px] leading-5 text-ink-muted" data-id="element-6677" data-mp="3mh0zdn3o2812">
              Disputed orders stay in escrow until a moderator resolves them. Release, refund and
              partial refund each write a new transaction rather than editing the original.
            </p>
          </div>
        </Panel>}

      {tab === 'Capabilities' && <Panel title="Regulatory readiness" description="Campuzon builds the ledger and transaction architecture; regulated activity is performed by licensed parties." bodyClassName="p-0" data-id="element-6678" data-mp="2p157125289di">
          <ul className="divide-y divide-line" data-id="element-6679" data-mp="19cp586bp6m7q">
            {capabilityGates.map(gate => <li key={gate.id} className="px-5 py-4" data-id="element-6680" data-mp="vh3dhre2xobm">
                <div className="flex flex-wrap items-center gap-2" data-id="element-6681" data-mp="10jc80s6loyfu">
                  <p className="text-[13px] font-semibold text-ink" data-id="element-6682" data-mp="1o84ve095pxyu">{gate.capability}</p>
                  <Badge tone={gate.status === 'live' ? 'ok' : 'warn'} data-id="element-6683" data-mp="1s9gmybblct9o">
                    {gate.status === 'live' ? 'Live' : 'Gated'}
                  </Badge>
                  <span className="text-[12px] text-ink-subtle" data-id="element-6684" data-mp="1e95tr1mt72lc">{gate.performedBy}</span>
                </div>
                <p className="mt-1.5 text-[13px] leading-6 text-ink-muted" data-id="element-6685" data-mp="1j6xpk6qkr8ut">{gate.note}</p>
              </li>)}
          </ul>
        </Panel>}
    </div>;
}